/* eslint-disable react/prop-types, complexity */
import React, { useEffect, useRef, useState } from 'react';

import { Row, Spin } from 'antd';
import cn from 'classnames';
import update from 'immutability-helper';
import { useSelector } from 'react-redux';

import ActivityTreePane from '#components/ActivityTreePane/ActivityTreePane.jsx';
import AnimatedSortable from '#components/AnimatedSortable/index.js';
import CustomPaginator from '#components/CustomPaginator/index.js';
import LookaheadActivity from '#components/LookaheadActivity/index.js';
import LookaheadOtherGroup from '#components/LookaheadOtherGroup/index.jsx';
import useSubtradeUser from '#hooks/useSubtradeUser.js';
import useWindowDimensions from '#hooks/useWindowDimensions.js';
import { lookaheadActions } from '#redux/actions/lookaheadActions.js';
import { ganttAPI } from '#utils/customGanttPlugin.js';
import { isFeatureOn } from '#utils/featureUtils.js';
import { calculateLeanStatus } from '#utils/lookahead-common.js';

export function TableView(props) {
  const {
    activeActivityTreePane,
    activities,
    antIcon,
    currentPage,
    deleteSubcontract,
    existFilter,
    groupBy,
    handleAddConstraint,
    isLoading,
    isReloadDataViews,
    lastLevelActivitiesCard,
    massiveSelection,
    onCloseCard,
    permission,
    recursivelySelection,
    setActivityResource,
    setCurrentPage,
    setMassiveSelection,
    setTaskSelected,
    setTypeResource,
    setVisibleFormResource,
    setVisibleFormSubcontract,
    setVisibleFormTags,
    showDrawer,
    subContracts,
    t,
    tableConfig,
    toSelectResponsables,
    toSelectTags,
    updateAsyncActivity,
    updateAsyncTask,
    updateRender
  } = props;
  const [array, setArray] = useState([]);
  const [totalTablesWidth, setTotalTablesWidth] = useState(0);
  const [scrollStates, setScrollStates] = useState({});

  let paginatorRef = useRef();
  const { height } = useWindowDimensions();
  const subtradeRole = useSubtradeUser();

  useEffect(() => {
    if (isLoading) return;

    setArray([<GeneralHeader key="header" {...props} />, ...activities]);
  }, [isLoading]);

  useEffect(() => {
    if (paginatorRef.current) {
      paginatorRef.current.resetAfterColumnIndex(0);
    }
  }, [totalTablesWidth]);

  useEffect(() => {
    if (tableConfig) {
      const tablesWidth = tableConfig.reduce(
        (sum, el) => (el.visible ? sum + el.width : sum),
        0
      );

      setTotalTablesWidth(tablesWidth);
    }
  }, [tableConfig]);

  if (groupBy.criteria === 'activity' || groupBy.criteria === 'activityId') {
    return (
      <div
        className={cn({
          'activity-pane__container':
            isFeatureOn('enable-LWP-103') &&
            activeActivityTreePane &&
            !isLoading
        })}>
        <WrappedActivityTreePane {...props} />
        {isReloadDataViews && (
          <div>
            <Spin
              className="loader-spinner-lookahead-table"
              indicator={antIcon}
            />
          </div>
        )}
        {!isReloadDataViews && (
          <CustomPaginator
            lookahead={true}
            scrollStates={scrollStates}
            totalTablesWidth={totalTablesWidth}
            massiveSelection={massiveSelection}
            resizing={false}
            current={currentPage}
            setCurrentPage={setCurrentPage}
            data={array}
            t={t}
            isFilterdData={existFilter}
            itemSize={height < 700 ? height * 0.55 : height * 0.68}
            renderItem={(item, key, virtualizeRef) => {
              if (key === 0) {
                paginatorRef = virtualizeRef;
                return item;
              }

              window.rowsActivities?.resetAfterRowIndex(0);
              return (
                <LookaheadActivity
                  subtradeRole={subtradeRole}
                  t={t}
                  permission={permission}
                  ganttAPI={ganttAPI}
                  isFilterdData={existFilter}
                  updateAsyncTask={updateAsyncTask}
                  updateAsyncActivity={updateAsyncActivity}
                  lastLevelActivities={lastLevelActivitiesCard}
                  scrollStates={scrollStates}
                  setScrollStates={setScrollStates}
                  virtualizeRef={virtualizeRef}
                  massiveSelectionHandler={recursivelySelection}
                  setMassiveSelection={setMassiveSelection}
                  resizing={false}
                  tableMetadata={tableConfig}
                  key={key}
                  lastLvlActivity={item}
                  index={key}
                  toSelectResponsables={toSelectResponsables}
                  handleAddConstraint={handleAddConstraint}
                  subContracts={subContracts}
                  setActivityResource={setActivityResource}
                  setTaskSelected={setTaskSelected}
                  setVisibleFormSubcontract={setVisibleFormSubcontract}
                  showDrawer={showDrawer}
                  onCloseCard={onCloseCard}
                  deleteSubcontract={deleteSubcontract}
                  calculateLeanStatus={calculateLeanStatus}
                  setVisibleFormResource={setVisibleFormResource}
                  setTypeResource={setTypeResource}
                  setVisibleFormTags={setVisibleFormTags}
                  toSelectTags={toSelectTags}
                />
              );
            }}
            perPage={1}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={cn({
        'activity-pane__container':
          isFeatureOn('enable-LWP-103') && activeActivityTreePane && !isLoading
      })}>
      <WrappedActivityTreePane {...props} />
      <CustomPaginator
        scrollStates={scrollStates}
        totalTablesWidth={totalTablesWidth}
        massiveSelection={massiveSelection}
        itemSize={height < 700 ? height * 0.55 : height * 0.68}
        current={currentPage}
        setCurrentPage={setCurrentPage}
        data={array}
        isFilterdData={existFilter}
        t={t}
        renderItem={(item, key, virtualizeRef) => {
          if (key === 0) {
            paginatorRef = virtualizeRef;
            return item;
          }
          window.rowsActivities?.resetAfterRowIndex(0);
          return (
            <LookaheadOtherGroup
              subtradeRole={subtradeRole}
              t={t}
              permission={permission}
              lookaheadActions={lookaheadActions}
              ganttAPI={ganttAPI}
              updateAsyncTask={updateAsyncTask}
              updateAsyncActivity={updateAsyncActivity}
              scrollStates={scrollStates}
              isFilterdData={existFilter}
              setScrollStates={setScrollStates}
              virtualizeRef={virtualizeRef}
              lastLvlActivity={item}
              data={activities}
              updateRender={updateRender}
              massiveSelectionHandler={recursivelySelection}
              setMassiveSelection={setMassiveSelection}
              tableMetadata={tableConfig}
              groupParent={item}
              index={key}
              key={key}
              showDrawer={showDrawer}
              lastLevelActivities={lastLevelActivitiesCard}
              onCloseCard={onCloseCard}
              toSelectResponsables={toSelectResponsables}
              handleAddConstraint={handleAddConstraint}
              subContracts={subContracts}
              setVisibleFormSubcontract={setVisibleFormSubcontract}
              groupBy={groupBy}
              setVisibleFormTags={setVisibleFormTags}
              toSelectTags={toSelectTags}
            />
          );
        }}
        perPage={1}
      />
    </div>
  );
}

function GeneralHeader({
  updateTableMetadata,
  listView,
  findView,
  defaultView,
  isLoading,
  tableConfig
}) {
  const projectState = useSelector((state) => state.projectState);

  const moveCard = async ({ oldIndex, newIndex }) => {
    const dragCard = tableConfig[oldIndex];

    const a = update(tableConfig, {
      $splice: [
        [oldIndex, 1],
        [newIndex, 0, dragCard]
      ]
    });
    updateTableMetadata(a);
  };

  const onDivResizeHandler = (width, colMetadata) => {
    if (!colMetadata.doubleClicked) return;

    colMetadata.width = width;
    colMetadata.doubleClicked = false;

    const findIdx = listView.current.findIndex(findView);

    defaultView.current.columns_json.settings.forEach((column) => {
      if (column.name === colMetadata.name) {
        column.width = width;
      }
    });

    listView.current[findIdx].view.columns_json.settings.forEach((column) => {
      if (column.name === colMetadata.name) {
        column.width = width;
      }
    });

    listView.current[findIdx].project = projectState.projectSelected;
    listView.current[findIdx].sector = projectState.sectorSelected;

    updateTableMetadata(defaultView.current.columns_json.settings, false, true);
  };

  const onDivResizeDoubleClicked = (col, val) => {
    col.doubleClicked = val;
  };

  if (isLoading) return null;

  return (
    <Row className="fixed-tree-table-header">
      <AnimatedSortable
        useDragHandle
        items={tableConfig}
        onSortEnd={moveCard}
        axis="x"
        onDivResizeHandler={onDivResizeHandler}
        onDivResizeDoubleClicked={onDivResizeDoubleClicked}
      />
    </Row>
  );
}

function WrappedActivityTreePane({
  activeActivityTreePane,
  activitiesTreePanel,
  currentActivityHidden,
  defaultView,
  filterSettings,
  getLookahead,
  isLoading,
  isReloadDataViews,
  setActiveActivityTreePane,
  setActivityTreePaneSettings,
  t
}) {
  if (!isFeatureOn('enable-LWP-103') || !activeActivityTreePane || isLoading)
    return null;

  return (
    <ActivityTreePane
      activities={activitiesTreePanel}
      actitySettings={defaultView.current.activityTreeSettings}
      closePane={setActiveActivityTreePane}
      filterSettings={filterSettings}
      onCommandDispach={getLookahead}
      viewId={defaultView.current.id}
      setActivityTreePaneSettings={setActivityTreePaneSettings}
      isReloadDataViews={isReloadDataViews}
      t={t}
      currentActivityHidden={currentActivityHidden}
    />
  );
}
